import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import GoBack from "../../components/GoBack";
import { RenewalProvider, useRenewal } from "./RenewalContext";
import { RenewalTestModeBanner } from "./renewalTestMode";

/**
 * Guards every renewal-concierge screen behind a resolved policy so each step
 * can assume `policy` is present. Sits inside RenewalProvider.
 */
/**
 * Narrower than the app's shared `max-w-5xl` container: these steps are a
 * single column of short form fields, and full width leaves the content
 * stranded. `mx-auto` re-centres within the wider main element.
 */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-auto w-full max-w-3xl">{children}</div>
);

const RenewalGate: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { loading, notFound, error, termUuid } = useRenewal();

  // Inside a flow (/renewal/:uuid/renew, /move-out, /not-sure) "Go Back"
  // should return to the flow picker, not jump all the way out to the
  // dashboard. On the landing page itself there's nowhere left to go but out.
  const landingPath = `/renewal/${termUuid}`;
  const inFlow = pathname.replace(/\/$/, "") !== landingPath;
  const backTo = inFlow ? landingPath : "/dashboard";

  if (loading) {
    return (
      <Shell>
        <GoBack to="/dashboard" />
        <p className="text-sm text-muted">Loading your renewal...</p>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <GoBack to="/dashboard" />
        <div className="rounded-xl bg-white p-10 text-center shadow-sm">
          <h3 className="text-lg font-semibold text-primary">
            Renewal not found
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            We couldn't find a renewal matching this link. It may have expired,
            or it may belong to a different account.
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="mt-6 cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
          >
            Back to dashboard
          </button>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <GoBack to="/dashboard" />
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <GoBack to={backTo} />
      <RenewalTestModeBanner termUuid={termUuid} />
      <Outlet />
    </Shell>
  );
};

const RenewalLayout: React.FC = () => (
  <RenewalProvider>
    <RenewalGate />
  </RenewalProvider>
);

export default RenewalLayout;
